import React from 'react';
import { Eye, Layers, ArrowRightFromLine } from 'lucide-react';

// Reusable 3-way ownership lens toggle (Created · Owned · Assigned).
// Single source of truth for the lens UI on both Command Center and
// Pipeline pages — keeps wording, testids and tooltip consistent.
//
// - "created":   sourcing lens — apps this person/team originally added.
// - "owned":     union — apps the team CREATED *or* now ASSIGNED to them.
// - "assigned":  handoff-only — apps assigned to the team that were sourced
//                by someone else (e.g. admin bulk-uploaded → handed off).
//
// Recruiters never see the toggle; the backend ignores `owner_lens` for them
// because they always see the strict-RBAC union (own + explicitly assigned).
// The toggle is hidden via the `visible` prop instead of conditional render
// upstream so layout doesn't jump when the user role flips.
const LENS_OPTIONS = [
  {
    value: 'created',
    label: 'Created',
    icon: Eye,
    tooltip: 'Candidates my team sourced (original uploader).',
  },
  {
    value: 'owned',
    label: 'Owned',
    icon: Layers,
    tooltip: 'Created by my team OR handed off to my team. Union view.',
  },
  {
    value: 'assigned',
    label: 'Assigned',
    icon: ArrowRightFromLine,
    tooltip: 'Only candidates handed off TO my team via assignment.',
  },
];

const OwnerLensToggle = ({
  value = 'created',
  onChange,
  visible = true,
  size = 'sm',
  testid = 'owner-lens-toggle',
}) => {
  if (!visible) return null;

  const sizeCls = size === 'xs'
    ? 'text-[10px] px-1.5 py-0.5'
    : 'text-xs px-2.5 py-1';

  return (
    <div className="inline-flex items-center gap-2" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 hidden sm:inline">
        Lens
      </span>
      <div className="inline-flex rounded-md border border-slate-200 bg-white overflow-hidden shadow-sm">
        {LENS_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => value !== opt.value && onChange?.(opt.value)}
              title={opt.tooltip}
              data-testid={`${testid}-${opt.value}`}
              className={`${sizeCls} font-semibold inline-flex items-center gap-1 transition border-r border-slate-200 last:border-r-0 ${
                active
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              aria-pressed={active}
            >
              <Icon className={size === 'xs' ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Convenience hook — persists the lens in localStorage so a manager's
// preference sticks across reloads and across navigation between Pipeline
// and Command Center. Each surface can use its own key if the lens should
// feel surface-specific; pass the same key to share across surfaces.
export const useStickyOwnerLens = (storageKey = 'owner_lens', initial = 'created') => {
  const [value, setValue] = React.useState(() => {
    try {
      const stored = window.localStorage.getItem(storageKey);
      if (stored === 'created' || stored === 'owned' || stored === 'assigned') {
        return stored;
      }
    } catch (_) { /* localStorage unavailable */ }
    return initial;
  });
  const update = React.useCallback((next) => {
    setValue(next);
    try { window.localStorage.setItem(storageKey, next); } catch (_) { /* ignore */ }
  }, [storageKey]);
  return [value, update];
};

export default OwnerLensToggle;
